import React, {useMemo} from 'react';
import ImportMode from './ImportMode';
import {NuvoImportResult} from './Types';
import {Button} from '@forecast-it/design-system';
import Spinner, {SpinnerStyle} from '../../shared/components/spinner/Spinner';

type SubmitDataImportProps = {
	importMode: ImportMode;
	newData: NuvoImportResult;
	existingData: {
		rateCards: string[];
		clients: string[];
		projects: string[];
		emails: string[];
	};
	onSubmit: () => void;
	onCancel: () => void;
	loading: boolean;
};

/**
 * @name normalizeEmail
 * @param s - The email to normalize.
 * @returns The normalized email.
 */
function normalizeEmail(s: string): string {
	return s.trim().toLowerCase();
}

/**
 * @name collectImportPersonEmails
 * @param newData
 * @param importTasks
 * @returns
 */
function collectImportPersonEmails(newData: NuvoImportResult, importTasks: boolean): string[] {
	const taskAssignedEmails = importTasks
		? newData
				.filter(
					(row): row is {task_assigned_comma_seperated_emails: string} => !!row.task_assigned_comma_seperated_emails
				)
				.flatMap(row => row.task_assigned_comma_seperated_emails.split(','))
		: [];

	const taskOwnerEmails = importTasks
		? newData.filter((row): row is {task_owner_email: string} => !!row.task_owner_email).map(row => row.task_owner_email)
		: [];

	const timeRegEmails = newData
		.filter((row): row is {time_registration_person_email: string} => !!row.time_registration_person_email)
		.map(row => row.time_registration_person_email);

	return [...taskOwnerEmails, ...taskAssignedEmails, ...timeRegEmails];
}

export const SubmitDataImport = ({
	importMode,
	newData,
	existingData,
	onSubmit,
	onCancel,
	loading,
}: SubmitDataImportProps): React.JSX.Element => {
	const importProjects = importMode === ImportMode.ImportNewProjects;
	const importTasks = importProjects || importMode === ImportMode.ImportPhasesTasksTimeRegs;

	const personsToCreate = useMemo(() => {
		const existingKeys = new Set(existingData.emails.map(normalizeEmail));
		const rawEmails = collectImportPersonEmails(newData, importTasks);
		const firstSeen = new Map<string, string>();
		for (const raw of rawEmails) {
			const key = normalizeEmail(raw);
			if (!key) {
				continue;
			}
			if (existingKeys.has(key)) {
				continue;
			}
			if (!firstSeen.has(key)) {
				firstSeen.set(key, raw.trim());
			}
		}
		return [...firstSeen.values()];
	}, [newData, existingData, importTasks]);

	const projectsToCreate = useMemo(
		() =>
			newData
				.filter((row): row is {project_name: string} => !!row.project_name)
				.map(row => row.project_name)
				.filter(name => {
					const projectIdMatch = name.match(/^P-?(\d+)$/);
					return projectIdMatch === null || !existingData.projects.includes(projectIdMatch[1]);
				})
				.reduce((curr, p) => (curr.includes(p) ? curr : [...curr, p]), [] as string[]),
		[newData, existingData]
	);

	const clientsToCreate = useMemo(
		() =>
			newData
				.filter((row): row is {project_client: string} => !!row.project_client)
				.map(row => row.project_client)
				.filter(client => !existingData.clients.includes(client))
				.reduce((curr, c) => (curr.includes(c) ? curr : [...curr, c]), [] as string[]),
		[newData, existingData]
	);
	const rateCardsToCreate = useMemo(
		() =>
			newData
				.filter((row): row is {project_rate_card: string} => !!row.project_rate_card)
				.map(row => row.project_rate_card)
				.filter(rc => !existingData.rateCards.includes(rc))
				.reduce((curr, rc) => (curr.includes(rc) ? curr : [...curr, rc]), [] as string[]),
		[newData, existingData]
	);

	const tasksToCreate = useMemo(
		() =>
			newData
				.filter((row): row is {project_name: string; task_name: string} => !!row.project_name && !!row.task_name)
				.map(row => row.project_name + '#####' + row.task_name)
				.reduce((curr, t) => (curr.includes(t) ? curr : [...curr, t]), [] as string[]),
		[newData, existingData]
	);

	const timeEntriesToCreate = useMemo(
		() => newData.filter(row => row.time_registration_minutes && row.time_registration_minutes > 0),
		[newData]
	);

	return (
		<div>
			<div className="sub-description">
				<a className="bold">{personsToCreate.length}</a> deactivated person(s) will be created.
				{personsToCreate.length > 0 ? <br></br> : null}
				{personsToCreate.length > 0 ? <a>{personsToCreate.join(', ')}</a> : null}
			</div>
			{importProjects ? (
				<div className="sub-description">
					<a className="bold">{clientsToCreate.length}</a> client(s) will be created.
					{clientsToCreate.length > 0 ? <br></br> : null}
					{clientsToCreate.length > 0 ? <a>{clientsToCreate.join(', ')}</a> : null}
				</div>
			) : null}
			{importProjects ? (
				<div className="sub-description">
					<a className="bold">{rateCardsToCreate.length}</a> rate card(s) will be created.
					{rateCardsToCreate.length > 0 ? <br></br> : null}
					{rateCardsToCreate.length > 0 ? <a>{rateCardsToCreate.join(', ')}</a> : null}
				</div>
			) : null}
			{importProjects ? (
				<div className="sub-description">
					<a className="bold">{projectsToCreate.length}</a> projects will be created.
				</div>
			) : null}
			{importTasks ? (
				<div className="sub-description">
					<a className="bold">{tasksToCreate.length}</a> tasks will be created.
				</div>
			) : null}
			<div className="sub-description">
				<a className="bold">{timeEntriesToCreate.length}</a> time entries will be created.
			</div>
			<div className="button">
				<Button onPress={onSubmit} disabled={loading} rightIcon={!loading ? 'upload' : undefined}>
					Confirm and upload data {loading ? <Spinner spinning style={SpinnerStyle.onLight} size={18} /> : undefined}
				</Button>
				<Button onPress={onCancel} disabled={loading} emphasis="medium">
					Cancel & restart
				</Button>
			</div>
		</div>
	);
};
